/*
 * File: CurrencyInput.tsx
 * Author: Austin Robinson
 * Date Created: 10/6/2023
 * Copyright (c) DrivingDoc 2023
 */
import { InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { useController, useFormContext } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

interface CurrencyInputProps {
  name: string;
  label: string;
  textFieldProps?: TextFieldProps;
}

const defaultTextFieldProps: TextFieldProps = {
  variant: 'standard',
  InputLabelProps: {
    shrink: true,
  },
  InputProps: {
    startAdornment: <InputAdornment position="start">$</InputAdornment>,
  },
};

export default function CurrencyInput({ name, label, textFieldProps = defaultTextFieldProps }: CurrencyInputProps) {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ control, name });

  return (
    <NumericFormat
      customInput={TextField}
      name={name}
      label={label}
      variant="standard"
      fullWidth
      slotProps={{
        input: {
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        },
        inputLabel: {
          shrink: true,
        },
      }}
      error={!!fieldState.error}
      helperText={fieldState.error?.message}
      getInputRef={field.ref}
      // @ts-expect-error: field.value is unknown, but it is safely at runtime
      value={typeof field.value === 'number' ? field.value / 100 : 0}
      onBlur={field.onBlur}
      onValueChange={(values) => {
        field.onChange((values?.floatValue ?? 0) * 100);
      }}
      allowLeadingZeros={false}
      allowNegative={false}
      decimalScale={2}
      fixedDecimalScale
      thousandsGroupStyle="thousand"
      thousandSeparator=","
      {...textFieldProps}
    />
  );
}
